import { useTranslation } from 'react-i18next'
import { Spinner } from 'components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import UnvModal from 'components/UnvDialog/UnvModal'
import UnvDrawer from 'components/UnvDialog/UnvDrawer'
import HistoryViewer from 'components/HistoryViewer/HistoryViewer'
import {
  listUsers,
  getUser,
  createUser,
  listRoles,
  grantRole,
  revokeRole,
  lockUser,
  unlockUser,
  resetPassword,
  resetPin,
} from 'service/AdminPanelService'
import styles from '../AdminFullPage.module.scss'

const PAGE_SIZE = 20

function genPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'
  let out = ''
  for (let i = 0; i < 10; i += 1) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function UsersTab({ notify }) {
  const { t } = useTranslation('common')

  const [items, setItems] = useState([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [role, setRole] = useState('')
  const [isActive, setIsActive] = useState('')
  const [loading, setLoading] = useState(false)

  const [roles, setRoles] = useState([])
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState({
    inn: '',
    surname: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role_codes: [],
    is_active: true,
  })
  const [createdInfo, setCreatedInfo] = useState(null)

  const [drawerUser, setDrawerUser] = useState(null)
  const [drawerLoading, setDrawerLoading] = useState(false)
  const [drawerTab, setDrawerTab] = useState('profile')

  const load = useCallback(() => {
    setLoading(true)
    const params = { page, page_size: PAGE_SIZE }
    if (q) params.q = q
    if (role) params.role = role
    if (isActive !== '') params.is_active = isActive
    listUsers(params)
      .then((res) => {
        const data = res?.data || res
        setItems(Array.isArray(data) ? data : data?.results || [])
        setCount(data?.count ?? (Array.isArray(data) ? data.length : 0))
      })
      .catch(() => {
        setItems([])
        setCount(0)
      })
      .finally(() => setLoading(false))
  }, [page, q, role, isActive])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    listRoles()
      .then((res) => {
        const data = res?.data || res
        setRoles(Array.isArray(data) ? data : data?.results || [])
      })
      .catch(() => setRoles([]))
  }, [])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((count || 0) / PAGE_SIZE)),
    [count],
  )

  const openDrawer = (id) => {
    setDrawerLoading(true)
    setDrawerTab('profile')
    getUser(id)
      .then((res) => setDrawerUser(res?.data || res))
      .catch(() => setDrawerUser(null))
      .finally(() => setDrawerLoading(false))
  }

  const refreshDrawer = () => {
    if (!drawerUser?.id) return
    getUser(drawerUser.id)
      .then((res) => setDrawerUser(res?.data || res))
      .catch(() => {})
  }

  const handleCreate = () => {
    const payload = { ...createForm }
    if (!payload.password) {
      payload.password = genPassword()
    }
    createUser(payload)
      .then((res) => {
        const data = res?.data || res
        setCreatedInfo({
          login: data?.username || data?.inn || createForm.inn,
          password: data?.generated_password || payload.password,
        })
        notify('Пользователь создан', 'success')
        setCreateForm({
          inn: '',
          surname: '',
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          role_codes: [],
          is_active: true,
        })
        load()
      })
      .catch((err) => {
        notify(
          err?.response?.data?.detail || 'Не удалось создать пользователя',
          'error',
        )
      })
  }

  const toggleRoleInForm = (code) => {
    setCreateForm((s) => {
      const set = new Set(s.role_codes)
      if (set.has(code)) set.delete(code)
      else set.add(code)
      return { ...s, role_codes: Array.from(set) }
    })
  }

  const onGrant = (code) => {
    if (!drawerUser?.id || !code) return
    grantRole(drawerUser.id, code)
      .then(() => {
        notify('Роль выдана', 'success')
        refreshDrawer()
      })
      .catch(() => notify('Не удалось выдать роль', 'error'))
  }

  const onRevoke = (code) => {
    if (!drawerUser?.id) return
    revokeRole(drawerUser.id, code)
      .then(() => {
        notify('Роль отозвана', 'success')
        refreshDrawer()
      })
      .catch(() => notify('Не удалось отозвать роль', 'error'))
  }

  const onLock = () => {
    if (!drawerUser?.id) return
    lockUser(drawerUser.id)
      .then(() => {
        notify('Пользователь заблокирован', 'success')
        refreshDrawer()
      })
      .catch(() => notify('Ошибка', 'error'))
  }

  const onUnlock = () => {
    if (!drawerUser?.id) return
    unlockUser(drawerUser.id)
      .then(() => {
        notify('Пользователь разблокирован', 'success')
        refreshDrawer()
      })
      .catch(() => notify('Ошибка', 'error'))
  }

  const onResetPassword = () => {
    if (!drawerUser?.id) return
    resetPassword(drawerUser.id)
      .then((res) => {
        const data = res?.data || res
        const pwd = data?.generated_password
        notify(
          pwd ? `Новый пароль: ${pwd}` : 'Пароль сброшен',
          'success',
        )
      })
      .catch(() => notify('Ошибка', 'error'))
  }

  const onResetPin = () => {
    if (!drawerUser?.id) return
    resetPin(drawerUser.id)
      .then(() => notify('ПИН сброшен', 'success'))
      .catch(() => notify('Ошибка', 'error'))
  }

  const userRoleCodes = (drawerUser?.roles || []).map((r) =>
    typeof r === 'string' ? r : r?.code,
  )
  const availableRolesToGrant = roles.filter(
    (r) => !userRoleCodes.includes(r.code),
  )

  return (
    <div>
      <h2 className={styles.section_title}>{t('pages.adminFullPage.tab_users')}</h2>

      <div className={styles.toolbar}>
        <input
          className="unv-input"
          placeholder={t('pages.usersTab.search_placeholder')}
          value={q}
          onChange={(e) => {
            setPage(1)
            setQ(e.target.value)
          }}
        />
        <select
          className="unv-select"
          value={role}
          onChange={(e) => {
            setPage(1)
            setRole(e.target.value)
          }}
        >
          <option value="">{t('pages.usersTab.all_roles')}</option>
          {roles.map((r) => (
            <option key={r.id || r.code} value={r.code}>
              {r.title || r.code}
            </option>
          ))}
        </select>
        <select
          className="unv-select"
          value={isActive}
          onChange={(e) => {
            setPage(1)
            setIsActive(e.target.value)
          }}
        >
          <option value="">{t('pages.ordersPage.all_statuses')}</option>
          <option value="true">{t('pages.usersTab.active')}</option>
          <option value="false">{t('pages.usersTab.inactive')}</option>
        </select>
        <div className={styles.toolbar_spacer} />
        <button
          type="button"
          className="unv-btn"
          onClick={() => {
            setCreatedInfo(null)
            setCreateOpen(true)
          }}
        >
          Создать пользователя
        </button>
      </div>

      {loading ? (
        <div className={styles.spinner_box}>
          <Spinner.Center />
        </div>
      ) : items.length === 0 ? (
        <div className="unv-empty">{t('pages.usersTab.not_found')}</div>
      ) : (
        <table className="unv-table unv-table--clickable">
          <thead>
            <tr>
              <th>ID</th>
              <th>{t('pages.usersTab.col_login')}</th>
              <th>{t('common.fullName')}</th>
              <th>Email</th>
              <th>{t('pages.adminFullPage.tab_roles')}</th>
              <th>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} onClick={() => openDrawer(u.id)}>
                <td>{u.id}</td>
                <td>{u.username || u.inn || '—'}</td>
                <td>
                  {u.full_name ||
                    [u.last_name, u.first_name].filter(Boolean).join(' ') ||
                    '—'}
                </td>
                <td>{u.email || '—'}</td>
                <td>
                  {(u.roles || []).map((r) => (
                    <span key={typeof r === 'string' ? r : r?.code} className="unv-badge" style={{ marginRight: 4 }}>
                      {typeof r === 'string' ? r : r?.title || r?.code}
                    </span>
                  ))}
                </td>
                <td>
                  {u.is_locked ? (
                    <span className="unv-badge unv-badge--danger">{t('pages.usersTab.locked')}</span>
                  ) : u.is_active === false ? (
                    <span className="unv-badge unv-badge--muted">{t('pages.usersTab.inactive_status')}</span>
                  ) : (
                    <span className="unv-badge unv-badge--success">{t('dpo.status_active')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="unv-pagination">
        <span>
          Всего: {count} • Стр. {page} из {totalPages}
        </span>
        <div className="unv-pagination__controls">
          <button
            type="button"
            className="unv-btn unv-btn--ghost unv-btn--sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Назад
          </button>
          <button
            type="button"
            className="unv-btn unv-btn--ghost unv-btn--sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Вперёд
          </button>
        </div>
      </div>

      
      <UnvModal
        open={createOpen}
        title={t('menuDrawer.create_user')}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button
              type="button"
              className="unv-btn unv-btn--ghost"
              onClick={() => setCreateOpen(false)}
            >
              Закрыть
            </button>
            {!createdInfo ? (
              <button type="button" className="unv-btn" onClick={handleCreate}>
                {t('common.create')}
              </button>
            ) : null}
          </>
        }
      >
        {createdInfo ? (
          <div>
            <p>{t('pages.usersTab.user_created_save')}</p>
            <div className={styles.password_box}>
              Логин: {createdInfo.login}
              <br />
              Пароль: {createdInfo.password}
            </div>
          </div>
        ) : (
          <div>
            <div className={styles.row}>
              <div style={{ flex: 1 }}>
                <label className="unv-label">{t('auth.inn')}</label>
                <input
                  className="unv-input"
                  value={createForm.inn}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, inn: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="unv-label">Email</label>
                <input
                  className="unv-input"
                  type="email"
                  value={createForm.email}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, email: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className={styles.row}>
              <div style={{ flex: 1 }}>
                <label className="unv-label">{t('user.last_name')}</label>
                <input
                  className="unv-input"
                  value={createForm.surname}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, surname: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="unv-label">{t('user.first_name')}</label>
                <input
                  className="unv-input"
                  value={createForm.first_name}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, first_name: e.target.value }))
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="unv-label">{t('user.middle_name')}</label>
                <input
                  className="unv-input"
                  value={createForm.last_name}
                  onChange={(e) =>
                    setCreateForm((s) => ({ ...s, last_name: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className={styles.row}>
              <div style={{ flex: 1 }}>
                <label className="unv-label">{t('pages.usersTab.password_optional')}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="unv-input"
                    value={createForm.password}
                    onChange={(e) =>
                      setCreateForm((s) => ({ ...s, password: e.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="unv-btn unv-btn--ghost"
                    onClick={() =>
                      setCreateForm((s) => ({ ...s, password: genPassword() }))
                    }
                  >
                    Сгенерировать
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.subsection}>
              <label className="unv-label">{t('pages.adminFullPage.tab_roles')}</label>
              <div className={styles.checkbox_list}>
                {roles.map((r) => (
                  <label key={r.id || r.code}>
                    <input
                      type="checkbox"
                      checked={createForm.role_codes.includes(r.code)}
                      onChange={() => toggleRoleInForm(r.code)}
                    />
                    {r.title || r.code}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </UnvModal>

      
      <UnvDrawer
        open={!!drawerUser}
        title={
          drawerUser
            ? drawerUser.full_name ||
              `${drawerUser.last_name || ''} ${drawerUser.first_name || ''}`.trim() ||
              drawerUser.username
            : ''
        }
        onClose={() => setDrawerUser(null)}
      >
        {drawerLoading ? (
          <div className={styles.spinner_box}>
            <Spinner.Center />
          </div>
        ) : drawerUser ? (
          <div>
            <div className="unv-tabs">
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'profile' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('profile')}
              >
                Профиль
              </button>
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'roles' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('roles')}
              >
                Роли
              </button>
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'actions' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('actions')}
              >
                Действия
              </button>
              <button
                type="button"
                className={`unv-tab ${drawerTab === 'history' ? 'unv-tab--active' : ''}`}
                onClick={() => setDrawerTab('history')}
              >
                История
              </button>
            </div>

            {drawerTab === 'profile' ? (
              <div className={styles.kv}>
                <div className={styles.kv_key}>ID</div>
                <div>{drawerUser.id}</div>
                <div className={styles.kv_key}>{t('common.login')}</div>
                <div>{drawerUser.username || '—'}</div>
                <div className={styles.kv_key}>Email</div>
                <div>{drawerUser.email || '—'}</div>
                <div className={styles.kv_key}>{t('user.first_name')}</div>
                <div>{drawerUser.first_name || '—'}</div>
                <div className={styles.kv_key}>{t('user.last_name')}</div>
                <div>{drawerUser.last_name || '—'}</div>
                <div className={styles.kv_key}>{t('common.status')}</div>
                <div>
                  {drawerUser.is_locked
                    ? 'Заблокирован'
                    : drawerUser.is_active === false
                      ? 'Неактивен'
                      : 'Активен'}
                </div>
              </div>
            ) : null}

            {drawerTab === 'roles' ? (
              <div>
                <h4 className={styles.section_title} style={{ fontSize: 15 }}>
                  {t('pages.usersTab.current_roles')}
                </h4>
                <div className={styles.role_chips}>
                  {(drawerUser.roles || []).length === 0 ? (
                    <span className="unv-badge unv-badge--muted">{t('pages.usersTab.no_roles')}</span>
                  ) : (
                    (drawerUser.roles || []).map((r) => {
                      const code = typeof r === 'string' ? r : r?.code
                      const title = typeof r === 'string' ? r : r?.title || r?.code
                      return (
                        <span key={code} className={styles.chip}>
                          {title}
                          <button type="button" onClick={() => onRevoke(code)}>
                            ×
                          </button>
                        </span>
                      )
                    })
                  )}
                </div>

                <h4
                  className={styles.section_title}
                  style={{ fontSize: 15, marginTop: 16 }}
                >
                  {t('pages.usersTab.grant_role')}
                </h4>
                <div className={styles.checkbox_list}>
                  {availableRolesToGrant.length === 0 ? (
                    <span className="unv-badge unv-badge--muted">
                      {t('pages.usersTab.all_granted')}
                    </span>
                  ) : (
                    availableRolesToGrant.map((r) => (
                      <button
                        key={r.id || r.code}
                        type="button"
                        className="unv-btn unv-btn--ghost unv-btn--sm"
                        onClick={() => onGrant(r.code)}
                      >
                        + {r.title || r.code}
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : null}

            {drawerTab === 'actions' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {drawerUser.is_locked ? (
                  <button type="button" className="unv-btn" onClick={onUnlock}>
                    {t('pages.usersTab.unlock')}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="unv-btn unv-btn--danger"
                    onClick={onLock}
                  >
                    {t('pages.usersTab.lock')}
                  </button>
                )}
                <button
                  type="button"
                  className="unv-btn unv-btn--ghost"
                  onClick={onResetPassword}
                >
                  {t('pages.usersTab.reset_password')}
                </button>
                <button
                  type="button"
                  className="unv-btn unv-btn--ghost"
                  onClick={onResetPin}
                >
                  {t('pages.usersTab.reset_pin')}
                </button>
              </div>
            ) : null}

            {drawerTab === 'history' ? (
              <HistoryViewer
                title={t('pages.usersTab.history_user')}
                q={drawerUser.username || drawerUser.email || ''}
                limit={100}
              />
            ) : null}
          </div>
        ) : null}
      </UnvDrawer>
    </div>
  )
}

export default UsersTab
